import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import StorageIcon from '@mui/icons-material/Storage';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
  BiotechRounded,
  BlurCircularRounded,
  ContentCutRounded,
  LocalShippingRounded,
  LuggageOutlined,
  ShareRounded,
  ScienceRounded,
  FolderRounded,
  ArticleRounded,
  ArchiveRounded,
  EmailRounded,
  AssignmentRounded,
  MemoryRounded,
  DescriptionRounded,
  SchoolRounded,
  MenuBookRounded,
  AssessmentRounded,
  AccountBalanceRounded,
  PhoneRounded,
  VideoCallRounded,
  ChatRounded,
  MeetingRoomRounded,
  PersonRounded,
  GroupRounded,
  CalendarTodayRounded,
  DateRangeRounded,
} from '@mui/icons-material';
import { TreeSorting, SampleSortDirection } from './treeSorting';
import { NODE_TYPES } from './treeNodeTypeConfigs';
import { IBackend } from '@/api/BackendApi';
import { log } from '@/lib/utils/logger';
import { formatErrorMessage } from '@/lib/utils/apiErrors';
import {
  SampleDto,
  SampleActivityDto,
  ProjectDto,
  ProjectActivityDto,
  CommunicationDto,
  UserSettingsDto,
  ActivityCategoryDto,
  ActivityTypeDto,
} from '@/api/ApiClient';
import {
  TreeNode,
  NodeType,
  NodeDataTypeMap,
  LoadContext,
  NodeDescriptor,
  NodeTypeConfig,
  NodeDataType,
} from '@/types/tree';

export const extractTypeFromNodeId = (nodeId: string | undefined): NodeType | undefined => {
  if (!nodeId) return undefined;
  const index = nodeId.indexOf('-');
  if (index === -1) return undefined;
  return nodeId.substring(0, index) as NodeType;
};

/**
 * Factory class for creating and managing tree nodes.
 * Implements lazy loading, caching, and error handling for tree operations.
 */
export class TreeNodeFactory {
  private _userSettings: UserSettingsDto | undefined;

  constructor(userSettings: UserSettingsDto | undefined) {
    this._userSettings = userSettings ?? {};
  }

  get userSettings(): UserSettingsDto | undefined {
    return this._userSettings;
  }

  /** Cache for node data to avoid redundant API calls */
  private static readonly nodeCache = new Map<
    string,
    {
      data: any;
      timestamp: number;
    }
  >();

  /** Cache expiration time in milliseconds (5 minutes) */
  private static readonly CACHE_EXPIRY = 5 * 60 * 1000;

  /**
   * Creates a new tree node with the given type and data.
   * @param type - The type of node to create
   * @param data - The data to associate with the node
   * @param parentId - Optional ID of the parent node
   * @returns A new TreeNode instance
   * @throws Error if the node type is not supported
   */
  createNode<T extends NodeType>(
    type: T,
    data: NodeDataTypeMap[T],
    parentId?: string,
    existingNodeIds?: Set<string>
  ): TreeNode {
    log.debug('[TreeNodeFactory] Creating node:', { type, dataId: data?.id, parentId });

    const config = NODE_TYPES[type];
    if (!config) {
      log.error('[TreeNodeFactory] Unsupported node type:', {
        type,
        availableTypes: Object.keys(NODE_TYPES),
      });
      throw new Error(`Unsupported node type: ${type}`);
    }

    const isExpandable = config.calculateInitialExpandable ? config.calculateInitialExpandable(data) : true;
    log.debug('[TreeNodeFactory] Node expandable state:', { type, isExpandable });

    const node = this.internalCreateNode(type, data, parentId, isExpandable, undefined, existingNodeIds);

    log.debug('[TreeNodeFactory] Created node:', { nodeId: node.nodeId, dataId: node.dataId, type, parentId });
    return node;
  }

  /**
   * Helper method to create a node with standard properties
   */
  private internalCreateNode<T extends NodeType>(
    type: T,
    data: NodeDataTypeMap[T],
    parentId: string | undefined = undefined,
    isExpandable: boolean = false,
    createNodeId: (data: NodeDataTypeMap[T]) => string = (data) => TreeNodeFactory.createNodeId(type, data),
    existingNodeIds?: Set<string>
  ): TreeNode {
    const dataId = createNodeId(data);
    const nodeId = existingNodeIds ? TreeNodeFactory.createUniqueNodeId(dataId, existingNodeIds) : dataId;

    // Note: isExpandable is now passed as parameter, calculated from config

    // Get unified display data (label and icon)
    const { label, icon, iconOverlay, iconOverlayTitle, iconMuted, canBeSelected } = TreeNodeFactory.getNodeDisplayData(
      type,
      data,
      this._userSettings
    );

    return {
      nodeId: nodeId,
      dataId: dataId, // Use the composite ID for dataId
      type,
      label,
      icon,
      iconOverlay,
      iconOverlayTitle,
      iconMuted,
      isExpandable: isExpandable,
      canBeSelected,
      expanded: false,
      children: [],
      parentId,
      dataVersion: 0, // Initialize version counter
    };
  }

  /**
   * Helper method to handle caching logic
   */
  private static async getCachedData<T>(cacheKey: string, loader: () => Promise<T>): Promise<T> {
    const cached = TreeNodeFactory.nodeCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < TreeNodeFactory.CACHE_EXPIRY) {
      log.debug('[TreeNodeFactory] Using cached data:', { cacheKey });
      return cached.data;
    }

    const data = await loader();
    TreeNodeFactory.nodeCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
    return data;
  }

  /** Invalidate cached data for a key. Call when changes (add/remove) make the cache stale. */
  static invalidateCache(cacheKey: string): void {
    TreeNodeFactory.nodeCache.delete(cacheKey);
    log.debug('[TreeNodeFactory] Invalidated cache:', { cacheKey });
  }

  /** Invalidate children cache for a node. Use when the node's children have changed (add/remove) or on explicit Reload. */
  static invalidateChildrenCache(nodeId: string): void {
    const prefix = `children-${nodeId}`;
    for (const key of TreeNodeFactory.nodeCache.keys()) {
      if (key === prefix || key.startsWith(`${prefix}:`)) {
        TreeNodeFactory.nodeCache.delete(key);
      }
    }
    log.debug('[TreeNodeFactory] Invalidated children cache:', { nodeId });
  }

  /**
   * Helper method to handle loading errors
   */
  private static handleLoadError(operation: string, nodeId: string, error: unknown): never {
    const message = formatErrorMessage(error);
    log.error(`[TreeNodeFactory] Error ${operation}:`, { nodeId, error: message });
    throw new Error(`Failed to ${operation} for node ${nodeId}: ${message}`);
  }

  /**
   * Loads children for a node, using caching when possible.
   *
   * Data is loaded into the store for each child node.
   *
   * @param node - The node to load children for
   * @param backend - The backend API client
   * @param getData - The function to get the data for a node from the store
   * @param setData - The function to set the data for a node in the store
   * @param childPredicate - A predicate to filter the children
   * @returns Promise that resolves to an array of child nodes and a boolean indicating if all children were loaded
   * @throws Error if loading fails
   */
  async loadChildren(
    node: TreeNode,
    backend: IBackend,
    getData: (dataId: string) => any,
    setData: (dataId: string, data: NodeDataType) => void,
    childPredicate: (childData: NodeDataType) => boolean = () => true
  ): Promise<{ children: TreeNode[]; allChildrenLoaded: boolean }> {
    log.debug('[TreeNodeFactory] Loading children for node:', { nodeId: node.nodeId, type: node.type });

    try {
      const config = NODE_TYPES[node.type];
      if (!config) {
        throw new Error(`No configuration found for node type: ${node.type}`);
      }

      // Get node data from the store
      const nodeData = getData(node.dataId);
      if (!nodeData) {
        throw new Error(`Node data not found for dataId: ${node.dataId}`);
      }

      // Create context for the config method
      const context: LoadContext = {
        isRoot: !node.parentId,
        parentId: node.parentId,
        parentType: node.parentId ? extractTypeFromNodeId(node.parentId) : undefined,
        userSettings: this._userSettings || {},
      };

      // Include parent context: nested under growth run skips parent-activity merge; root does not.
      // Caching by nodeId alone reused nested (no Syn) results when opening the piece alone.
      const childrenCacheKey = `children-${node.nodeId}:${context.parentType ?? 'root'}`;
      const descriptors = await TreeNodeFactory.getCachedData(childrenCacheKey, () =>
        config.loadChildren(node.dataId, nodeData, backend, context)
      );

      // Create actual nodes from descriptors
      const children = descriptors
        .filter((descriptor) => childPredicate(descriptor.data))
        .map((descriptor) => {
          // Use standard node creation - this will store the data in Zustand
          const createdNode = this.createNode(descriptor.type, descriptor.data, node.nodeId);
          setData(createdNode.dataId, descriptor.data);

          // Mark node as needing data reload if specified
          if (descriptor.isFullyLoaded === false) {
            createdNode.needsDataReload = true;
          }

          return createdNode;
        });

      // Now sort using the data that was stored by createNode
      return {
        children: this.sort(children, getData),
        allChildrenLoaded: children.length === descriptors.length,
      };
    } catch (error: unknown) {
      TreeNodeFactory.handleLoadError('loading children', node.nodeId, error);
    }
  }

  async loadChildrenDescriptors<T extends NodeType>(
    node: TreeNode<T>,
    backend: IBackend,
    getData: (dataId: string) => NodeDataTypeMap[T]
  ): Promise<NodeDescriptor<NodeType>[]> {
    log.debug('[TreeNodeFactory] Loading children descriptors for node:', {
      nodeId: node.nodeId,
      type: node.type,
      dataId: node.dataId,
    });

    try {
      const config = NODE_TYPES[node.type];
      if (!config) {
        throw new Error(`No configuration found for node type: ${node.type}`);
      }

      // Get node data from the store
      const nodeData = getData(node.dataId);
      if (!nodeData) {
        throw new Error(`Node data not found for dataId: ${node.dataId}`);
      }

      // Create context for the config method
      const context: LoadContext = {
        isRoot: !node.parentId,
        parentId: node.parentId,
        parentType: node.parentId ? extractTypeFromNodeId(node.parentId) : undefined,
        userSettings: this._userSettings || {},
      };

      // Get descriptors from config (cached). Call invalidateChildrenCache when changes occur.
      const childrenCacheKey = `children-${node.nodeId}:${context.parentType ?? 'root'}`;
      const descriptors = await TreeNodeFactory.getCachedData(childrenCacheKey, () =>
        config.loadChildren(node.dataId, nodeData, backend, context)
      );

      return descriptors;
    } catch (error: unknown) {
      TreeNodeFactory.handleLoadError('loading children descriptors', node.nodeId, error);
    }
  }

  sort(nodes: TreeNode[], getData: (dataId: string) => any): TreeNode[] {
    const sampleSortDirection: SampleSortDirection = this._userSettings?.sortNewestSampleFirst
      ? SampleSortDirection.NEW_TO_OLD
      : SampleSortDirection.OLD_TO_NEW;
    return TreeSorting.sort(nodes, getData, sampleSortDirection);
  }

  /**
   * Loads notes for a node, using caching when possible.
   * @param node - The node to load notes for
   * @param backend - The backend API client
   * @returns Promise that resolves to an array of notes
   * @throws Error if loading fails
   */
  async loadNotes(node: TreeNode, backend: IBackend): Promise<any[]> {
    log.debug('[TreeNodeFactory] Loading notes for node:', { nodeId: node.nodeId, type: node.type });

    try {
      const config = NODE_TYPES[node.type];
      if (!config || !config.loadNotes || !config.canHaveNotes) {
        log.debug('[TreeNodeFactory] Node type does not support notes, skipping load:', { type: node.type });
        return [];
      }

      return await TreeNodeFactory.getCachedData(`notes-${node.nodeId}`, () => config.loadNotes!(node.dataId, backend));
    } catch (error: unknown) {
      TreeNodeFactory.handleLoadError('loading notes', node.nodeId, error);
    }
  }

  /**
   * Loads files for a node from the API (no in-memory cache).
   * File lists must stay fresh: snapshot builds replace file rows (new PKs) while activity ids can stay the same;
   * caching previously led to DELETE sending stale live file ids and the server failing to resolve the file.
   * @param node - The node to load files for
   * @param backend - The backend API client
   * @returns Promise that resolves to an array of files
   * @throws Error if loading fails
   */
  async loadFiles(node: TreeNode, backend: IBackend): Promise<any[]> {
    log.debug('[TreeNodeFactory] Loading files for node:', { nodeId: node.nodeId, type: node.type });

    try {
      const config = NODE_TYPES[node.type];
      if (!config || !config.loadFiles || !config.canHaveFiles) {
        log.debug('[TreeNodeFactory] Node type does not support files, skipping load:', { type: node.type });
        return [];
      }

      return await config.loadFiles!(node.dataId, backend);
    } catch (error: unknown) {
      TreeNodeFactory.handleLoadError('loading files', node.nodeId, error);
    }
  }

  /**
   * Clears the cache for a specific node or all nodes.
   * @param nodeId - Optional node ID to clear cache for
   */
  clearCache(nodeId?: string): void {
    if (nodeId) {
      // Clear all cache entries for this node
      for (const key of TreeNodeFactory.nodeCache.keys()) {
        if (key.includes(nodeId)) {
          TreeNodeFactory.nodeCache.delete(key);
        }
      }
    } else {
      // Clear entire cache
      TreeNodeFactory.nodeCache.clear();
    }
  }

  public static getNodeConfig<T extends NodeType>(type: T): NodeTypeConfig<NodeDataTypeMap[T]> {
    return NODE_TYPES[type];
  }

  public static createNodeId(type: NodeType, data: NodeDataTypeMap[NodeType]): string {
    switch (type) {
      case 'sample':
        if (!data?.id || !(data as SampleDto).sampleLabel) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${(data as SampleDto).sampleLabel}`;
      case 'sampleAggregate':
        if (!data?.id || !(data as SampleDto).sampleLabel) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${(data as SampleDto).sampleLabel}`;
      case 'sampleSnapshot':
        // Use database PK for snapshots to ensure uniqueness
        if (!data?.id) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${data.id}`;
      case 'sampleInPackage':
        // Same dataId as live `sample` so package browse shares sample DTO cache with /samples.
        if (!data?.id || !(data as SampleDto).sampleLabel) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `sample-${(data as SampleDto).sampleLabel}`;
      case 'sampleActivityAggregate':
      case 'sampleActivityInPackage':
      case 'sampleActivitySnapshot':
      case 'theoryActivityInPackage':
        // Use database PK for packaged / aggregate activities
        if (!data?.id) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${data.id}`;
      case 'projectInData':
      case 'publicationInData':
        // Use database PK for read-only projects/publications
        if (!data?.id) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${data.id}`;
      case 'project':
      case 'communications':
      case 'oldSamples':
      case 'newSamples':
        if (!data?.id || !(data as ProjectDto).projectLabel) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${(data as ProjectDto).projectLabel}`;
      case 'dataPackage':
        if (!data?.id) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${data.id}`;
      default:
        if (!data?.id) {
          throw new Error(`Node data must have an 'id' property: ${JSON.stringify(data)}`);
        }
        return `${type}-${data.id}`;
    }
  }

  /**
   * Creates a unique node ID for a given data ID.
   * If multiple nodes need to reference the same entity data, this ensures uniqueness.
   * @param dataId - The composite data ID (e.g., "sample-ABC123")
   * @param existingNodeIds - Set of existing node IDs to avoid collisions
   * @returns A unique node ID
   */
  public static createUniqueNodeId(dataId: string, existingNodeIds: Set<string>): string {
    let nodeId = dataId;
    let counter = 1;

    while (existingNodeIds.has(nodeId)) {
      nodeId = `${dataId}-${counter}`;
      counter++;
    }

    return nodeId;
  }
  /**
   * Gets the label for a node type, using the getTypeLabel function if available.
   * @param node - The node to get the label for
   * @returns The label for the node type
   */
  public static getNodeTypeLabel(node: TreeNode, getData?: (dataId: string) => any): string {
    const config = NODE_TYPES[node.type];

    // Helper function for fallback: capitalize the node type
    const getFallbackLabel = () => node.type.charAt(0).toUpperCase() + node.type.slice(1);

    if (!config?.getTypeLabel) {
      return getFallbackLabel();
    }

    const nodeData = getData ? getData(node.dataId) : null;
    // Call getTypeLabel with data if available, or undefined if not
    // The parameter is optional, so implementations can be either (data?: T) => string or () => string
    return config.getTypeLabel(nodeData ?? undefined) ?? getFallbackLabel();
  }

  /**
   * Gets unified display data for a node (label and icon)
   * This combines the logic from GetNodeData in iconMappings and getLabel from NODE_TYPES
   * @param type - Type of the node
   * @param data - Data associated with the node
   * @returns Object containing label, icon, and optional icon overlay metadata
   */
  public static getNodeDisplayData(
    type: NodeType,
    data: any,
    userSettings?: UserSettingsDto
  ): {
    label: string;
    icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
    iconOverlay?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
    iconOverlayTitle?: string;
    iconMuted?: boolean;
    canBeSelected: boolean;
  } {
    // Get the label from NODE_TYPES config
    const config = NODE_TYPES[type];
    const label = config ? config.getLabel(data, userSettings) : 'Unknown';
    const canBeSelected = config ? config.canBeSelected : true;

    // Get the icon based on type and data (logic from GetNodeData)
    let icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
    let iconOverlay: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> | undefined;
    let iconOverlayTitle: string | undefined;
    let iconMuted: boolean | undefined;

    switch (type) {
      case 'sample':
      case 'sampleAggregate':
      case 'sampleInPackage':
      case 'sampleSnapshot':
        const sample = data as SampleDto;
        icon = BlurCircularRounded;
        // KO parity: sample disc + badge (suitcase/share), not the shipping-folder truck icon.
        if (sample.shipped) {
          iconOverlay = LuggageOutlined;
          iconOverlayTitle = 'Shipped';
          iconMuted = true;
        } else if (sample.transferred) {
          iconOverlay = ShareRounded;
          iconOverlayTitle = 'Transferred';
        }
        break;
      case 'sampleActivity':
      case 'sampleActivityAggregate':
      case 'sampleActivityInPackage':
      case 'sampleActivitySnapshot':
        const sampleActivity = data as SampleActivityDto;
        switch (sampleActivity.category) {
          case ActivityCategoryDto.Processing:
            switch (sampleActivity.type) {
              case ActivityTypeDto.Dicing:
                icon = ContentCutRounded;
                break;
              default:
                icon = ScienceRounded;
                break;
            }
            break;
          case ActivityCategoryDto.Characterization:
            icon = BiotechRounded;
            break;
          case ActivityCategoryDto.Storage:
            icon = StorageIcon;
            break;
          case ActivityCategoryDto.Transfer:
            icon = SwapHorizIcon;
            break;
          case ActivityCategoryDto.Inventory:
            icon = InventoryIcon;
            break;
          default:
            icon = HelpIcon;
            break;
        }
        break;
      case 'project':
      case 'projectInData':
        icon = FolderRounded; // Projects are like folders containing samples and activities
        break;
      case 'publication':
      case 'publicationInData':
        icon = MenuBookRounded; // Publications use book icon (KO parity)
        break;
      case 'dataPackage':
        icon = ArchiveRounded; // Data packages are like archives containing data
        break;
      case 'communication':
        // Select icon based on communication format
        const communicationData = data as CommunicationDto;
        const format = communicationData?.format?.toLowerCase() || '';

        if (format.includes('email') || format.includes('mail')) {
          icon = EmailRounded;
        } else if (format.includes('phone') || format.includes('call') || format.includes('telephone')) {
          icon = PhoneRounded;
        } else if (
          format.includes('video') ||
          format.includes('zoom') ||
          format.includes('teams') ||
          format.includes('meet')
        ) {
          icon = VideoCallRounded;
        } else if (
          format.includes('chat') ||
          format.includes('message') ||
          format.includes('slack') ||
          format.includes('discord')
        ) {
          icon = ChatRounded;
        } else if (format.includes('meeting') || format.includes('conference') || format.includes('presentation')) {
          icon = MeetingRoomRounded;
        } else if (format.includes('interview') || format.includes('one-on-one')) {
          icon = PersonRounded;
        } else if (format.includes('group') || format.includes('team') || format.includes('workshop')) {
          icon = GroupRounded;
        } else {
          icon = EmailRounded; // Default fallback
        }
        break;
      case 'projectActivity':
        const projectActivity = data as ProjectActivityDto;
        switch (projectActivity.type) {
          case ActivityTypeDto.Award:
            icon = AccountBalanceRounded; // Grant/funding - bank/institution icon
            break;
          case ActivityTypeDto.Decision:
            icon = AssessmentRounded; // Decision/assessment icon
            break;
          case ActivityTypeDto.Document:
            icon = DescriptionRounded; // Document icon
            break;
          case ActivityTypeDto.Presentation:
            icon = SchoolRounded; // Presentation/lecture icon
            break;
          case ActivityTypeDto.Publication:
            icon = MenuBookRounded; // Publication activity uses book icon (KO parity)
            break;
          case ActivityTypeDto.Proposal:
            icon = AssignmentRounded; // Proposal/task icon
            break;
          case ActivityTypeDto.Report:
            icon = ArticleRounded; // Report/article icon
            break;
          default:
            icon = AssignmentRounded; // Default for other project activities
            break;
        }
        break;
      case 'theoryActivity':
      case 'theoryActivityInPackage':
        icon = MemoryRounded;
        break;
      case 'simulations':
        icon = MemoryRounded; // Use same icon as theory activities since simulations are theory activities
        break;
      case 'simulationDataset':
        icon = ArchiveRounded; // Use archive icon for datasets
        break;
      case 'communications':
        icon = ChatRounded;
        break;
      case 'oldSamples':
        icon = BlurCircularRounded;
        break;
      case 'newSamples':
        icon = BlurCircularRounded;
        break;
      case 'shippings':
      case 'shipping':
        icon = LocalShippingRounded;
        break;
      case 'sampleYearGroup':
        icon = CalendarTodayRounded; // Calendar icon for year-based grouping
        break;
      case 'sampleQuarterGroup':
        icon = DateRangeRounded; // Date range icon for quarter-based grouping
        break;
      default:
        icon = HelpIcon;
        break;
    }

    return { label, icon, iconOverlay, iconOverlayTitle, iconMuted, canBeSelected };
  }

  /** Merges display metadata from getNodeDisplayData onto an existing tree node. */
  public static withNodeDisplayData<T extends TreeNode>(
    node: T,
    display: ReturnType<typeof TreeNodeFactory.getNodeDisplayData>
  ): T {
    return {
      ...node,
      label: display.label,
      icon: display.icon,
      iconOverlay: display.iconOverlay,
      iconOverlayTitle: display.iconOverlayTitle,
      iconMuted: display.iconMuted,
    };
  }
}
